import { Fragment, useRef } from 'react';
import { Disclosure } from '@headlessui/react';
import { ChevronDownIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { NavGroup } from '../types/doc';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDocs } from '../hooks/useDocs';

interface Props {
  nav?: NavGroup;
  onNavigate?: () => void;
}

interface DocItemProps {
  doc: {
    title: string;
    slug: string;
    sections?: Array<{ title: string; slug: string }>;
  };
  sortedSections: Array<{ title: string; slug: string }>;
  isDocActive: boolean;
  hash: string;
  onNavigate?: () => void;
  fullDoc?: {
    sections: Array<{ slug: string; order: number }>;
  };
}

const DocItem = ({ doc, sortedSections, isDocActive, hash, onNavigate, fullDoc }: DocItemProps) => {
  const navigate = useNavigate();
  const toggleButtonRef = useRef<HTMLButtonElement>(null);

  return (
    <Disclosure key={`${doc.slug}-${isDocActive}`} defaultOpen={isDocActive}>
      {({ open, close }) => {
        // Handle doc title click - toggle dropdown or navigate
        const handleDocClick = (e: React.MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          if (sortedSections.length > 0) {
            if (isDocActive) {
              // Already on this doc, toggle the disclosure
              if (open) {
                close();
              } else {
                // Open by clicking the Disclosure.Button
                toggleButtonRef.current?.click();
              }
            } else {
              // Navigate to doc with first section hash - sections will auto-open via defaultOpen
              navigate(`/docs/${doc.slug}#${sortedSections[0].slug}`);
              onNavigate?.();
            }
          } else {
            // No sections, just navigate to doc
            navigate(`/docs/${doc.slug}`);
            onNavigate?.();
          }
        };

        return (
          <Fragment>
            <div className="flex items-center justify-between rounded-lg px-3 py-2 text-sm font-medium text-slate-800 transition dark:text-slate-200">
              <button
                type="button"
                onClick={handleDocClick}
                className={clsx(
                  'flex flex-1 items-center gap-2 rounded-lg px-2 py-1 text-left',
                  isDocActive
                    ? 'text-brand-600 dark:text-brand-300'
                    : 'text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800'
                )}
              >
                <DocumentTextIcon className="h-4 w-4 text-brand-500" />
                <span>{doc.title}</span>
              </button>
              {sortedSections.length ? (
                <Disclosure.Button
                  ref={toggleButtonRef}
                  as="button"
                  className="rounded-full p-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <ChevronDownIcon
                    className={clsx('h-4 w-4 transition', open ? 'rotate-180' : '')}
                  />
                </Disclosure.Button>
              ) : null}
            </div>
            {sortedSections.length ? (
              <Disclosure.Panel className="mt-1 space-y-1 pl-5">
                {sortedSections.map((section) => {
                  const isSectionActive = isDocActive && hash === section.slug;
                  return (
                    <Link
                      key={section.slug}
                      to={`/docs/${doc.slug}#${section.slug}`}
                      onClick={(e) => {
                        onNavigate?.();
                        
                        // If clicking on the same section, just scroll
                        if (isDocActive && hash === section.slug) {
                          e.preventDefault();
                          const element = document.getElementById(section.slug);
                          if (element) {
                            requestAnimationFrame(() => {
                              const offset = 100;
                              const elementPosition = element.getBoundingClientRect().top;
                              const offsetPosition = elementPosition + window.pageYOffset - offset;
                              window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                            });
                          }
                          return;
                        }
                        
                        // If on same doc but different section, manually trigger scroll after navigation
                        if (isDocActive && hash !== section.slug) {
                          // Update hash immediately for smooth experience
                          window.location.hash = section.slug;
                          e.preventDefault();
                          
                          // Scroll to the section
                          setTimeout(() => {
                            const element = document.getElementById(section.slug);
                            if (element) {
                              const offset = 100;
                              const elementPosition = element.getBoundingClientRect().top;
                              const offsetPosition = elementPosition + window.pageYOffset - offset;
                              window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                            }
                          }, 50);
                          
                          // Update URL via React Router for proper state management
                          navigate(`/docs/${doc.slug}#${section.slug}`, { replace: true });
                        }
                        // For different docs, let React Router handle it naturally
                      }}
                      className={clsx(
                        'block rounded px-3 py-1.5 text-sm transition',
                        isSectionActive
                          ? 'bg-brand-50 text-brand-600 dark:bg-slate-800 dark:text-brand-300 font-medium'
                          : 'text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800'
                      )}
                    >
                      {section.title}
                    </Link>
                  );
                })}
              </Disclosure.Panel>
            ) : null}
          </Fragment>
        );
      }}
    </Disclosure>
  );
};

const SidebarNav = ({ nav, onNavigate }: Props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const pathParts = location.pathname.split('/');
  const currentDocSlug = pathParts[2]; // /docs/:slug
  const hash = location.hash.substring(1); // Remove # from hash
  const { data: allDocs } = useDocs();

  if (!nav) {
    return (
      <div className="animate-pulse space-y-3">
        {[...Array(4)].map((_, idx) => (
          <div key={idx} className="h-4 rounded bg-slate-200 dark:bg-slate-800" />
        ))}
      </div>
    );
  }

  return (
    <nav className="space-y-4">
      <div className="mb-4">
        <h2 className="text-sm font-bold text-slate-900 dark:text-white">Documentation</h2>
      </div>
      {Object.entries(nav).map(([group, docs]) => (
        <div key={group}>
          <p className="px-3 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            {group}
          </p>
          <div className="mt-2 space-y-2">
            {docs.map((doc) => {
              // Get full doc data to access section order
              const fullDoc = allDocs?.find((d) => d.slug === doc.slug);
              const sortedSections = doc.sections?.length
                ? [...doc.sections].sort((a, b) => {
                    // Try to get order from full doc data
                    const sectionA = fullDoc?.sections.find((s) => s.slug === a.slug);
                    const sectionB = fullDoc?.sections.find((s) => s.slug === b.slug);
                    
                    if (sectionA?.order !== undefined && sectionB?.order !== undefined) {
                      return sectionA.order - sectionB.order;
                    }
                    // Fallback to alphabetical sorting
                    return a.title.localeCompare(b.title);
                  })
                : [];
              
              // Determine if doc is active (when viewing any of its sections)
              const isDocActive = currentDocSlug === doc.slug;
              
              return (
                <DocItem
                  key={doc.slug}
                  doc={doc}
                  sortedSections={sortedSections}
                  isDocActive={isDocActive}
                  hash={hash}
                  onNavigate={onNavigate}
                  fullDoc={fullDoc}
                />
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );
};

export default SidebarNav;

